import { pick } from '@chakra-ui/utils';

import {
  getPartStyleFromStyleInterpolation,
  getStyleObjectFromStyleInterpolation,
  type StyleFunctionProps,
  type StyleObjectMulti,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { INPUT_STYLE_CONFIG } from '../input/inputStyleConfig';
import { SEARCH_INPUT_PARTS } from './searchInputParts';
import type {
  SearchInputColorScheme,
  SearchInputPart,
  SearchInputSize,
  SearchInputStyleConfig,
  SearchInputVariant,
} from './types';

const { defaultProps } = INPUT_STYLE_CONFIG;

const baseStyle: SearchInputStyleConfig['baseStyle'] = ({
  theme,
  colorMode,
}) => {
  const baseFieldStyles = getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.baseStyle,
    'field',
    { theme, colorMode },
  );

  return {
    container: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
    },
    field: {
      ...baseFieldStyles,
      borderRadius: 'md',
      flexGrow: 1,
      letterSpacing: 'normal',
    },
    element: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: 2,
    },
    hintWrapper: {
      ...pick(baseFieldStyles ?? {}, ['fontFamily', 'fontWeight']),
      borderWidth: '0px 1px',
      borderColor: 'transparent',
      borderStyle: 'solid',
      letterSpacing: 'normal',
      display: 'inline-block',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      position: 'absolute',
      top: '0px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    hint: {
      color: 'feature.secondary',
    },
  };
};

const getBaseSizeStyles = (
  size: SearchInputSize,
  {
    theme,
    colorMode,
  }: StyleFunctionProps<SearchInputVariant, SearchInputColorScheme>,
): StyleObjectMulti<SearchInputPart> => {
  const inputStyles = getStyleObjectFromStyleInterpolation(
    INPUT_STYLE_CONFIG.sizes[size],
    { theme, colorMode },
  );

  const inputFieldStyles = inputStyles.field;
  const inputElementStyles = inputStyles.element;

  return {
    hintWrapper: pick(inputStyles.field ?? {}, ['h', 'fontSize', 'lineHeight']),
    field: {
      ...inputFieldStyles,
      paddingInlineStart: '',
      paddingInlineEnd: '',
    },
    element: {
      ...inputElementStyles,
      px: inputFieldStyles?.px,
    },
  };
};

const SIZES: SearchInputStyleConfig['sizes'] = {
  sm: (styleFunctionProps) => {
    const base = getBaseSizeStyles('sm', styleFunctionProps);

    return {
      ...base,
      hintWrapper: {
        ...base.hintWrapper,
        py: 0.25,
        // Correct for misalignment that occurs in certain screen resolutions. See also the transform in the 'lg' size.
        transform: 'translateY(-0.5px)',
        '@media (min-resolution: 2x)': {
          transform: 'translateY(0.5px)',
        },
      },
      element: {
        ...base.element,
        gap: 0.5,
      },
    };
  },
  md: (styleFunctionProps) => {
    const base = getBaseSizeStyles('md', styleFunctionProps);

    return {
      ...base,
      hintWrapper: {
        ...base.hintWrapper,
        py: 0.5,
      },
      element: {
        ...base.element,
        gap: 0.75,
      },
    };
  },
  lg: (styleFunctionProps) => {
    const base = getBaseSizeStyles('lg', styleFunctionProps);

    return {
      ...base,
      hintWrapper: {
        ...base.hintWrapper,
        py: 1,
        transform: 'translateY(0.5px)',
      },
      element: {
        ...base.element,
        gap: 1,
      },
    };
  },
};

export const SEARCH_INPUT_STYLE_CONFIG: SearchInputStyleConfig = {
  parts: SEARCH_INPUT_PARTS,
  baseStyle,
  sizes: SIZES,
  defaultProps,
};
